import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MessageCircle,
  Send,
  X,
  Minimize2,
  Maximize2,
  Heart,
  RefreshCw,
  User,
  Bot,
  Sparkles,
  HelpCircle,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  ChevronRight
} from "lucide-react";

export default function GoalsOfCareChatbot({ 
  patientContext = null,
  referralId = null,
  minimized = false,
  onToggle = null,
  embedded = false 
}) {
  const [isOpen, setIsOpen] = useState(!minimized);
  const [isMinimized, setIsMinimized] = useState(minimized);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [starters, setStarters] = useState([]);
  const [resources, setResources] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadStarters();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadStarters = async () => {
    try {
      const response = await api.get("/goals-of-care/starters");
      setStarters(response.data.starters || []);
      setResources(response.data.resources || []);
    } catch (error) {
      console.error("Failed to load starters:", error);
      // Default starters
      setStarters([
        "What does hospice care actually provide?",
        "How do I know if hospice is right for my loved one?",
        "What's the difference between palliative care and hospice?",
        "How do I talk to my family about end-of-life wishes?"
      ]);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || inputValue.trim();
    if (!text) return;

    // Add user message
    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setSuggestedQuestions([]);

    try {
      const response = await api.post("/goals-of-care/chat", {
        message: text,
        session_id: sessionId,
        patient_context: patientContext,
        referral_id: referralId
      });

      setSessionId(response.data.session_id);

      // Add assistant message
      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setSuggestedQuestions(response.data.suggested_questions || []);

    } catch (error) {
      toast.error("Failed to get response. Please try again.");
      // Remove user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleStarterClick = (starter) => {
    sendMessage(starter);
  };

  const handleSuggestedClick = (question) => {
    sendMessage(question);
  };

  const resetConversation = () => {
    setMessages([]);
    setSessionId(null);
    setSuggestedQuestions([]);
    loadStarters();
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (onToggle) onToggle(!isMinimized);
  };

  if (!isOpen && !embedded) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 shadow-lg z-50"
        data-testid="goc-chat-trigger"
      >
        <Heart className="h-6 w-6" />
      </Button>
    );
  }

  if (isMinimized && !embedded) {
    return (
      <div 
        className="fixed bottom-6 right-6 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-lg shadow-xl p-3 cursor-pointer z-50"
        onClick={toggleMinimize}
        data-testid="goc-chat-minimized"
      >
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5" />
          <span className="font-medium">Goals of Care Assistant</span>
          <Maximize2 className="h-4 w-4 ml-2" />
        </div>
      </div>
    );
  }

  const chatContent = (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white p-4 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Heart className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold">Goals of Care Assistant</h3>
              <p className="text-xs text-rose-100">Compassionate guidance for care decisions</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20 h-8 w-8"
              onClick={resetConversation}
              title="New conversation"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {!embedded && (
              <>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:bg-white/20 h-8 w-8"
                  onClick={toggleMinimize}
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:bg-white/20 h-8 w-8"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4" style={{ height: embedded ? "400px" : "350px" }}>
        {messages.length === 0 ? (
          <div className="space-y-4">
            {/* Welcome message */}
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm p-3 max-w-[85%]">
                <p className="text-sm">
                  Hello! I'm here to help you navigate hospice care decisions with compassion and understanding. 
                  I can answer questions about goals of care, comfort measures, and how to support your loved ones 
                  through this journey. How can I help you today?
                </p>
              </div>
            </div>

            {/* Conversation starters */}
            <div className="mt-4">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Suggested questions:
              </p>
              <div className="flex flex-wrap gap-2">
                {starters.slice(0, 4).map((starter, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="text-xs h-auto py-2 px-3 whitespace-normal text-left border-rose-200 hover:bg-rose-50 hover:border-rose-300"
                    onClick={() => handleStarterClick(starter)}
                  >
                    {starter}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
              >
                {msg.role === "assistant" && (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                <div 
                  className={`rounded-2xl p-3 max-w-[85%] ${
                    msg.role === "user" 
                      ? "bg-rose-500 text-white rounded-tr-sm" 
                      : "bg-gray-100 rounded-tl-sm"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === "user" && (
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gray-100 rounded-2xl rounded-tl-sm p-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Suggested follow-up questions */}
            {suggestedQuestions.length > 0 && !isLoading && (
              <div className="mt-2 ml-11">
                <p className="text-xs text-muted-foreground mb-2">Follow-up questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((q, idx) => (
                    <Button
                      key={idx}
                      variant="ghost"
                      size="sm"
                      className="text-xs h-auto py-1 px-2 text-rose-600 hover:bg-rose-50"
                      onClick={() => handleSuggestedClick(q)}
                    >
                      <ChevronRight className="h-3 w-3 mr-1" />
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Resources footer */}
      {resources.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-t">
          <p className="text-xs text-muted-foreground mb-1">Helpful resources:</p>
          <div className="flex flex-wrap gap-2">
            {resources.slice(0, 2).map((resource, idx) => (
              <a 
                key={idx}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1"
              >
                {resource.title}
                <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="p-4 border-t bg-white rounded-b-xl">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about goals of care..."
            disabled={isLoading}
            className="flex-1"
            data-testid="goc-chat-input"
          />
          <Button 
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            className="bg-rose-500 hover:bg-rose-600"
            data-testid="goc-chat-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          This assistant provides general information only. Always consult your healthcare team for medical advice.
        </p>
      </div>
    </>
  );

  if (embedded) {
    return (
      <Card className="h-full" data-testid="goc-chat-embedded">
        <div className="flex flex-col h-full">
          {chatContent}
        </div>
      </Card>
    );
  }

  return (
    <div 
      className="fixed bottom-6 right-6 w-[380px] bg-white rounded-xl shadow-2xl z-50 flex flex-col"
      style={{ maxHeight: "calc(100vh - 100px)" }}
      data-testid="goc-chat-widget"
    >
      {chatContent}
    </div>
  );
}
